// -----------------------------------------------------------------------
// PublicProductionView — owner spec 2026-07-08l
// Read-only public page for a Full-Frame production. Opened via
// /share/production/:token by anyone who has the link (no auth). Shows:
//   • Title + client
//   • Master location + per-date location overrides
//   • Schedule (5 days, each with items list)
//   • Crew roster grouped by category (role, name, phone, days, hours,
//     meal preference, kashrut)
// Nothing here saves or edits — every value is displayed as static text.
// -----------------------------------------------------------------------
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicProduction } from "./api";
import { FullFrameLogo } from "./FullFrameLogo";

// ---------- helpers ----------
const fmtIL = (iso) => {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length < 3) return iso;
  return `${parseInt(parts[2], 10)}.${parseInt(parts[1], 10)}`;
};
const hebWeekday = (iso) => {
  if (!iso) return "";
  const parts = (iso || "").split("-").map((p) => parseInt(p, 10));
  if (parts.length < 3 || parts.some(isNaN)) return "";
  const d = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  if (isNaN(d.getTime())) return "";
  const names = ["יום א׳", "יום ב׳", "יום ג׳", "יום ד׳", "יום ה׳", "יום ו׳", "שבת"];
  return names[d.getUTCDay()] || "";
};
const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const waHref = (p) => {
  const d = onlyDigits(p);
  if (!d) return null;
  const intl = d.startsWith("0") ? `972${d.slice(1)}` : d;
  return `https://wa.me/${intl}`;
};
const telHref = (p) => (onlyDigits(p) ? `tel:${onlyDigits(p)}` : null);
const MEAL = { meat: { e: "🥩", l: "בשרי" }, veg: { e: "🥗", l: "צמחוני" }, vegan: { e: "🌱", l: "טבעוני" } };

const BUILTIN_CATEGORIES = [
  { key: "cast_directing",     name: "שחקנים / בימוי",             head: "from-blue-700 to-blue-500" },
  { key: "production",         name: "הפקה",                       head: "from-orange-600 to-amber-500" },
  { key: "location_equipment", name: "לוקיישן / צילום וציוד",       head: "from-emerald-600 to-teal-500" },
  { key: "art_wardrobe",       name: "אמנות / תלבושות",             head: "from-rose-600 to-pink-500" },
  { key: "post",               name: "פוסט / עריכה",                head: "from-violet-600 to-purple-500" },
];

export default function PublicProductionView() {
  const { token } = useParams();
  const [prod, setProd] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPublicProduction(token);
        if (!cancelled) setProd(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "לא נמצא");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const dayHeaders = useMemo(() => {
    const sched = prod?.schedule || [];
    if (sched.length > 0) {
      return sched.slice(0, 5).map((d) => ({
        date: fmtIL(d.date) || d.label || "יום",
        weekday: hebWeekday(d.date),
        iso: d.date || "",
      }));
    }
    return [];
  }, [prod?.schedule]);

  const categories = useMemo(() => {
    if (!prod) return [];
    const overrides = prod.category_names || {};
    const customs = prod.custom_categories || [];
    const themeByKey = Object.fromEntries(BUILTIN_CATEGORIES.map((c) => [c.key, c]));
    const builtIn = BUILTIN_CATEGORIES.map((c) => ({
      ...c,
      name: overrides[c.key] || c.name,
    }));
    const customBlocks = customs.map((cc) => {
      const src = themeByKey[cc.theme_key] || BUILTIN_CATEGORIES[0];
      return { key: cc.key, name: overrides[cc.key] || cc.name, head: src.head };
    });
    return [...builtIn, ...customBlocks];
  }, [prod]);

  const grouped = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.key] = [];
    for (const r of prod?.crew_roster || []) {
      const cat = m[r.category] ? r.category : (categories[0]?.key || "");
      if (m[cat]) m[cat].push(r);
    }
    return m;
  }, [prod?.crew_roster, categories]);

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-stone-950 text-stone-300">
        <div className="animate-pulse text-lg">טוען…</div>
      </div>
    );
  }
  if (error || !prod) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-stone-950 text-stone-300 p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-extrabold text-white mb-2">הלינק לא פעיל</h1>
          <p className="text-stone-400 text-sm">
            הלינק לא קיים או שההפקה הוסרה. פנה למי ששלח לך אותו.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-stone-950 text-stone-100" data-testid="ff-public-view">
      {/* --------- Top bar --------- */}
      <div className="border-b border-white/10 bg-stone-900/60 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <FullFrameLogo />
          <div className="text-[11px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 rounded-full px-3 py-1">
            צפייה בלבד
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* --------- Title --------- */}
        <section>
          <div className="text-xs uppercase tracking-widest text-stone-400 font-bold">הפקה</div>
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold mt-1 text-white leading-tight" data-testid="ff-public-name">
            {prod.name || "הפקה"}
          </h1>
          {prod.client_name && (
            <p className="text-stone-300 mt-2">
              לקוח: <span className="text-white font-semibold">{prod.client_name}</span>
            </p>
          )}
        </section>

        {/* --------- Contact --------- */}
        {(prod.contact_name || prod.client_phone || prod.client_email || prod.client_whatsapp) && (
          <section className="rounded-2xl border border-white/10 bg-stone-800/60 p-4">
            <h2 className="text-sm font-extrabold text-stone-300 uppercase tracking-widest mb-2">
              איש קשר
            </h2>
            <div className="flex flex-wrap gap-3 text-sm">
              {prod.contact_name && <div className="text-white font-bold">{prod.contact_name}</div>}
              {prod.client_phone && (
                <a href={telHref(prod.client_phone)} className="text-emerald-300 hover:text-emerald-200 font-bold" dir="ltr">
                  📞 {prod.client_phone}
                </a>
              )}
              {prod.client_whatsapp && (
                <a href={waHref(prod.client_whatsapp)} target="_blank" rel="noreferrer" className="text-emerald-300 hover:text-emerald-200 font-bold" dir="ltr">
                  💬 {prod.client_whatsapp}
                </a>
              )}
              {prod.client_email && (
                <a href={`mailto:${prod.client_email}`} className="text-sky-300 hover:text-sky-200 font-bold" dir="ltr">
                  ✉️ {prod.client_email}
                </a>
              )}
            </div>
          </section>
        )}

        {/* --------- Locations --------- */}
        {(prod.location || Object.keys(prod.location_by_day || {}).length > 0) && (
          <section className="rounded-2xl border border-amber-500/30 bg-amber-950/20 p-4" data-testid="ff-public-locations">
            <h2 className="text-sm font-extrabold text-amber-300 uppercase tracking-widest mb-2">
              📍 לוקיישן
            </h2>
            {prod.location && (
              <div className="text-white font-bold mb-2">{prod.location}</div>
            )}
            {dayHeaders.length > 0 && Object.keys(prod.location_by_day || {}).length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mt-2">
                {dayHeaders.map((h, i) => {
                  const perDay = prod.location_by_day?.[i] ?? prod.location_by_day?.[String(i)] ?? "";
                  if (!perDay) return null;
                  return (
                    <div key={i} className="bg-black/30 rounded-lg p-2 border border-amber-400/25">
                      <div className="text-[10px] font-extrabold text-amber-200">{h.date} · {h.weekday}</div>
                      <div className="text-xs text-white mt-1">{perDay}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* --------- Schedule --------- */}
        {(prod.schedule || []).length > 0 && (
          <section className="rounded-2xl border border-white/10 bg-stone-800/60 p-4" data-testid="ff-public-schedule">
            <h2 className="text-sm font-extrabold text-stone-300 uppercase tracking-widest mb-3">
              🗓️ לוח זמנים
            </h2>
            <div className="space-y-3">
              {(prod.schedule || []).map((day, idx) => (
                <div key={day.id || idx} className="rounded-xl border border-white/10 bg-stone-900/70 overflow-hidden">
                  <div className="px-4 py-2 bg-gradient-to-l from-pink-600 to-rose-500 text-white font-extrabold flex items-center gap-3 flex-wrap">
                    <span>יום {idx + 1}</span>
                    <span className="opacity-90">{fmtIL(day.date)}</span>
                    {hebWeekday(day.date) && <span className="text-amber-200">· {hebWeekday(day.date)}</span>}
                    {day.label && <span className="text-white/80 font-normal">· {day.label}</span>}
                  </div>
                  <div className="p-3 space-y-1.5 text-sm">
                    {(day.items || []).length === 0 ? (
                      <div className="text-stone-500 text-xs">אין פריטים</div>
                    ) : (
                      (day.items || []).map((it, j) => (
                        <div key={it.id || j} className="flex items-start gap-2 border-b border-white/5 pb-1 last:border-0">
                          {it.time && <span className="text-amber-300 font-bold tabular-nums whitespace-nowrap">{it.time}</span>}
                          <span className="text-stone-100">{it.text || it.title || ""}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* --------- Crew --------- */}
        {(prod.crew_roster || []).length > 0 && (
          <section className="space-y-4" data-testid="ff-public-crew">
            <h2 className="text-sm font-extrabold text-stone-300 uppercase tracking-widest">
              👥 צוות הפקה
            </h2>
            {categories.map((cat) => {
              const rows = grouped[cat.key] || [];
              if (rows.length === 0) return null;
              return (
                <div key={cat.key} className="rounded-2xl border border-white/10 bg-stone-800/60 overflow-hidden">
                  <div className={`px-4 py-2 bg-gradient-to-l ${cat.head} text-white font-extrabold flex items-center gap-2`}>
                    <span>{cat.name}</span>
                    <span className="text-xs opacity-80 font-normal">({rows.length})</span>
                  </div>
                  <div className="p-3 space-y-2">
                    {rows.map((r) => (
                      <PublicCrewRow key={r.id} row={r} dayHeaders={dayHeaders} locationByDay={prod.location_by_day} masterLocation={prod.location} />
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        <footer className="text-center text-stone-500 text-xs pt-6">
          FlowUp · Full Frame — צפייה בלבד
        </footer>
      </main>
    </div>
  );
}

function PublicCrewRow({ row, dayHeaders, locationByDay, masterLocation }) {
  const meal = MEAL[row.meal_pref || "meat"] || MEAL.meat;
  const compactHours = (h) => {
    if (!h) return "";
    const trim = (s) => (s || "").replace(/:00$/, "");
    const s = trim(h.start);
    const e = trim(h.end);
    if (!s && !e) return "";
    return `${s || "?"}-${e || "?"}`;
  };

  return (
    <div className="rounded-lg border border-white/10 bg-stone-900/60 p-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {row.role && <span className="text-xs text-stone-400 font-bold">{row.role}</span>}
          {row.name && <span className="text-white font-extrabold">{row.name}</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {row.phone && (
            <>
              <a href={telHref(row.phone)} className="text-emerald-300 hover:text-emerald-200 text-xs font-bold" dir="ltr">
                📞 {row.phone}
              </a>
              <a href={waHref(row.phone)} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300 text-xs font-bold">
                💬
              </a>
            </>
          )}
          <span className="text-[10px] bg-black/40 rounded px-2 py-0.5 font-bold">
            {meal.e} {meal.l}
          </span>
          {row.kosher && (
            <span className="text-[10px] bg-violet-600/40 text-white rounded px-2 py-0.5 font-bold">
              כשרות: {row.kosher}
            </span>
          )}
        </div>
      </div>

      {/* Days grid */}
      {dayHeaders.length > 0 && (
        <div className="grid grid-cols-5 gap-1.5 mt-2">
          {dayHeaders.map((h, i) => {
            const active = !!row.days?.[i];
            const hRange = active ? compactHours(row.hours?.[i] || row.hours?.[String(i)]) : "";
            const perDayLoc = locationByDay?.[i] ?? locationByDay?.[String(i)] ?? "";
            const loc = active ? (perDayLoc || masterLocation || "") : "";
            return (
              <div
                key={i}
                className={`rounded-md border text-center py-1 px-0.5 ${active ? "bg-amber-500/20 border-amber-400/40" : "bg-black/20 border-white/10 opacity-40"}`}
              >
                <div className={`text-[10px] font-extrabold ${active ? "text-amber-200" : "text-stone-500"}`}>
                  {h.date}
                </div>
                {h.weekday && (
                  <div className={`text-[9px] font-bold ${active ? "text-amber-300/80" : "text-stone-600"}`}>
                    {h.weekday}
                  </div>
                )}
                {hRange && (
                  <div className="text-[10px] font-extrabold text-white mt-0.5 tabular-nums">
                    {hRange}
                  </div>
                )}
                {loc && (
                  <div className="text-[8px] font-bold text-emerald-300 mt-0.5 break-words leading-snug">
                    {loc}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
